import React from "react";
import { HOME } from "@/constants/testIds";
import { SIZE_GUIDE } from "@/lib/site-data";
import HelpMePickWizard from "@/components/site/HelpMePickWizard";
import TextPhotoCta from "@/components/site/TextPhotoCta";
import { Sparkle } from "@phosphor-icons/react";

// Visual bin proportions roughly by size for hierarchy
const BIN_HEIGHTS = {
  "6 yd³": "h-20",
  "10 yd³": "h-24",
  "14 yd³": "h-28",
  "16 yd³": "h-32",
  "20 yd³": "h-36",
};

function BinIllustration({ size }) {
  return (
    <div className="flex items-end h-44">
      <div className="w-full relative">
        {/* Bin body */}
        <div className={`w-full ${BIN_HEIGHTS[size] || "h-24"} bg-slate-900 relative overflow-hidden`}>
          {/* slats */}
          <div className="absolute inset-y-2 left-3 right-3 grid grid-cols-6 gap-1">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-slate-700" />
            ))}
          </div>
          {/* sky-blue label band */}
          <div className="absolute bottom-2 left-2 right-2 h-5 bg-sky-500 flex items-center px-2">
            <span className="text-[9px] font-extrabold tracking-[0.2em] uppercase text-white">Troya</span>
          </div>
        </div>
        {/* angled lid suggestion */}
        <div className="absolute -top-2 left-0 right-0 h-2 bg-slate-700" />
      </div>
    </div>
  );
}

export default function SizesShowcase() {
  return (
    <section data-testid={HOME.sizesSection} id="sizes" className="bg-slate-50 border-b-2 border-slate-200">
      <div className="mx-auto max-w-7xl px-6 lg:px-8 py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <div className="text-[11px] font-extrabold tracking-[0.22em] uppercase text-sky-600">Bin sizes</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-slate-900 mt-3 leading-tight">
              Five sizes. One that&apos;s right for your project.
            </h2>
          </div>
          <p className="lg:col-span-5 text-slate-600 leading-relaxed lg:pt-8">
            Roll-off bins designed for tight Toronto driveways. Low-profile sides for easy loading. Driver guidance on
            placement &mdash; no broken curbs, no torn-up lawns.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {SIZE_GUIDE.map((b) => (
            <div
              key={b.size}
              className="group bg-white border-2 border-slate-900 hover:border-sky-500 transition-colors flex flex-col"
            >
              <div className="border-b-2 border-slate-900 group-hover:border-sky-500 p-6 bg-white">
                <BinIllustration size={b.size} />
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <div className="font-display font-black text-2xl text-slate-900">{b.size}</div>
                <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-sky-600 mt-1">
                  Best for
                </div>
                <div className="text-sm text-slate-600 mt-3 leading-relaxed flex-1">{b.best}</div>
                <a
                  href="#pricing"
                  className="mt-5 inline-block text-xs font-bold uppercase tracking-[0.18em] text-slate-900 group-hover:text-sky-600 transition-colors border-b-2 border-slate-900 group-hover:border-sky-500 pb-1 w-fit"
                >
                  Get a quote →
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Sizing guide CTA + Text-us-a-photo lane */}
        <div className="mt-10 grid lg:grid-cols-2 gap-5">
          <HelpMePickWizard
            trigger={
              <button
                type="button"
                data-testid="open-help-me-pick-sizes"
                className="group text-left border-2 border-sky-500 bg-gradient-to-r from-sky-50 via-white to-sky-50 hover:bg-sky-500 hover:text-white transition-all duration-200 p-5 flex items-center gap-4"
              >
                <div className="h-11 w-11 bg-sky-500 group-hover:bg-white group-hover:text-sky-500 text-white flex items-center justify-center shrink-0 transition-colors">
                  <Sparkle size={20} weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-extrabold tracking-[0.22em] uppercase text-sky-600 group-hover:text-white/90">
                    Sizing guide · 30 seconds
                  </div>
                  <div className="font-display font-bold text-base sm:text-lg text-slate-900 group-hover:text-white leading-tight mt-0.5">
                    Not sure what size you need? Help me pick.
                  </div>
                  <div className="text-xs text-slate-600 group-hover:text-white/90 mt-1">
                    Answer 3 quick questions and we&apos;ll match a bin to your job.
                  </div>
                </div>
              </button>
            }
          />
          <TextPhotoCta testid="text-photo-cta-sizes" />
        </div>
      </div>
    </section>
  );
}
